import { Box, Button, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { TTableStructureFilter } from 'ui/EasTable/types';
import { TPageResponse } from 'redux/types';
import { PAGE_SIZE } from 'services/constants/env';
import { getEasInfoGroupsRequest, getEasInfoListRequest } from 'redux/reducers/easInfo/reducer';
import {
	getEasInfoGroups,
	getEasInfoListData,
	getEasInfoListLoading,
} from 'redux/reducers/easInfo/selectors';
import { TEasInfoItem } from 'redux/reducers/easInfo/types';
import { GROUP_FILTER_SELECTOR, tableStructure } from './tableStructure';
import { EditSettingDialog } from './EditSettingDialog';
import { TEasInfoTableRow } from './types';
import { mapEasInfoToTableRows } from './utils';

const GROUP_FILTER_PARAM = `f-${GROUP_FILTER_SELECTOR}`;

const EDIT_ACTION = 'Edit';

export const EasProjectSettings: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);

	const easInfoList = useSelector(getEasInfoListData);
	const easInfoListLoading = useSelector(getEasInfoListLoading);
	const groups = useSelector(getEasInfoGroups);

	const [dialogOpen, setDialogOpen] = useState(false);
	const [settingToEdit, setSettingToEdit] = useState<TEasInfoItem | null>(null);

	const currentPage = Number(navigateParams.page) || 1;
	const searchString = navigateParams.search ? String(navigateParams.search) : '';
	const selectedGroupTitle = navigateParams[GROUP_FILTER_PARAM]
		? String(navigateParams[GROUP_FILTER_PARAM])
		: '';

	const structure = useMemo(() => {
		const groupOptions: TTableStructureFilter = (groups ?? []).map((group): [string, string] => [
			group.groupTitle,
			group.groupTitle,
		]);

		return tableStructure.map((column) =>
			column.selector === GROUP_FILTER_SELECTOR ? { ...column, filter: groupOptions } : column,
		);
	}, [groups]);

	useEffect(() => {
		dispatch(getEasInfoGroupsRequest());
	}, [dispatch]);

	useEffect(() => {
		dispatch(
			getEasInfoListRequest({
				pageNumber: currentPage,
				pageSize: PAGE_SIZE,
				searchString: searchString || null,
				groupTitle: selectedGroupTitle || null,
			}),
		);
	}, [dispatch, currentPage, searchString, selectedGroupTitle]);

	const rows = useMemo(() => mapEasInfoToTableRows(easInfoList?.data ?? []), [easInfoList]);

	const tableItems = useMemo<TPageResponse<TEasInfoTableRow> | null>(() => {
		if (!easInfoList) return null;

		return {
			pageNumber: easInfoList.pageNumber,
			pageSize: easInfoList.pageSize,
			totalCount: easInfoList.totalCount,
			data: rows,
		};
	}, [easInfoList, rows]);

	const handleAction = useCallback(
		(id: number, action: string) => {
			if (action !== EDIT_ACTION) return;

			const row = rows.find((item) => item.id === id) ?? null;
			if (!row) return;

			const { id: _id, ...setting } = row;
			setSettingToEdit(setting);
			setDialogOpen(true);
		},
		[rows],
	);

	const handleAddNew = useCallback(() => {
		setSettingToEdit(null);
		setDialogOpen(true);
	}, []);

	const handleDialogClose = useCallback(() => {
		setDialogOpen(false);
		setSettingToEdit(null);
	}, []);

	return (
		<Box sx={{ margin: '5px' }}>
			<EditSettingDialog
				open={dialogOpen}
				onClose={handleDialogClose}
				settingToEdit={settingToEdit}
				groups={groups}
			/>
			<EasTableWithFilter<TEasInfoTableRow>
				tableStructure={structure}
				items={tableItems}
				itemsIsLoading={easInfoListLoading}
				handleAction={handleAction}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary">
						Resources / Reference Tables / <b>EAS Project Settings</b>
					</Typography>
					<Button variant="contained" color="primary" size="small" onClick={handleAddNew}>
						Add new parameter
					</Button>
				</Box>
			</EasTableWithFilter>
		</Box>
	);
};
